import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../styles/cookie-consent.css";

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      const savedPreferences = JSON.parse(cookieConsent);
      setPreferences(savedPreferences);
    }
  }, []);

  const acceptAllCookies = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
    
    // Initialize tracking scripts here
    initializeTrackingScripts(allAccepted);
    
    // Dispatch custom event to notify ChatWidget
    window.dispatchEvent(new Event('cookiesAccepted'));
  };

  const rejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    setShowBanner(false);
    
    // Initialize only essential scripts
    initializeTrackingScripts(essentialOnly);
    
    // Dispatch custom event to notify ChatWidget
    window.dispatchEvent(new Event('cookiesAccepted'));
  };

  const savePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Initialize tracking scripts based on preferences
    initializeTrackingScripts(preferences);
    
    // Dispatch custom event to notify ChatWidget
    window.dispatchEvent(new Event('cookiesAccepted'));
  };

  const initializeTrackingScripts = (prefs) => {
    // Google Analytics
    if (prefs.analytics) {
      window.gtag = window.gtag || function() {
        (window.gtag.q = window.gtag.q || []).push(arguments);
      };
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
    }

    // Marketing/Advertising
    if (prefs.marketing) {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted'
        });
      }
    } else {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied'
        });
      }
    }

    // Functional cookies
    if (prefs.functional) {
      // Enable functional features like location services, language preferences
      console.log('Functional cookies enabled');
    }
  };

  const handlePreferenceChange = (type, value) => {
    if (type === 'essential') return; // Cannot disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!showBanner) return null;

  if (showPreferences) {
    return (
      <div className="cookie-consent-overlay">
        <div className="cookie-preferences-modal">
          <h3>Cookie Preferences</h3>
          <p>We use cookies to enhance your experience on our website. You can manage your preferences below:</p>
          
          <div className="cookie-category">
            <div className="cookie-category-header">
              <label>
                <input
                  type="checkbox"
                  checked={preferences.essential}
                  disabled={true}
                />
                <strong>Essential Cookies</strong>
              </label>
              <span className="required-badge">Required</span>
            </div>
            <p>These cookies are necessary for the website to function and cannot be switched off.</p>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <label>
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                />
                <strong>Functional Cookies</strong>
              </label>
            </div>
            <p>These cookies enable enhanced functionality and personalization, such as remembering your preferences.</p>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <label>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                />
                <strong>Analytics Cookies</strong>
              </label>
            </div>
            <p>These cookies help us understand how you use our website so we can improve it.</p>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <label>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                />
                <strong>Marketing Cookies</strong>
              </label>
            </div>
            <p>These cookies are used to show you relevant advertisements based on your interests.</p>
          </div>

          <div className="cookie-preferences-actions">
            <button onClick={savePreferences} className="save-preferences-btn">
              Save Preferences
            </button>
            <button onClick={() => setShowPreferences(false)} className="cancel-btn">
              Cancel
            </button>
          </div>
          
          <p className="cookie-policy-link">
            Learn more in our <Link to="/cookie-policy">Cookie Policy</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cookie-consent-banner">
      <div className="cookie-consent-content">
        <div className="cookie-consent-text">
          <h4>🍪 We use cookies</h4>
          <p>
            We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
            Some cookies are essential for the proper functioning of our healthcare platform.
          </p>
        </div>
        <div className="cookie-consent-actions">
          <button onClick={acceptAllCookies} className="accept-all-btn">
            Accept All
          </button>
          <button onClick={() => setShowPreferences(true)} className="manage-preferences-btn">
            Manage Preferences
          </button>
          <button onClick={rejectNonEssential} className="reject-btn">
            Reject Non-Essential
          </button>
        </div>
      </div>
      <div className="cookie-policy-link">
        <Link to="/cookie-policy">View our Cookie Policy</Link>
      </div>
    </div>
  );
}

export default CookieConsent; 